(function attachLineBossPricing(root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.LineBossPricing = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function createLineBossPricing() {
  function safeNumber(value, maximum = Number.POSITIVE_INFINITY) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? Math.min(Math.max(parsed, 0), maximum) : 0;
  }

  /**
   * Calculates customer and internal amounts from one source of truth.
   * Margin is recovered with cost / (1 - margin), not cost * (1 + margin).
   * Complexity applies to production and skilled work, never raw material.
   */
  function calculateQuote(input = {}) {
    const quantity = Math.max(1, Math.floor(safeNumber(input.quantity, 10000) || 1));
    const materialRate = safeNumber(input.materialRate);
    const grams = safeNumber(input.grams);
    const printHours = safeNumber(input.printHours);
    const designHours = input.designIncluded ? safeNumber(input.designHours) : 0;
    const designRate = safeNumber(input.designRate);
    const handsMinutes = safeNumber(input.handsMinutes);
    const handsRate = safeNumber(input.handsRate);
    const complexity = safeNumber(input.complexity, 5) || 1;
    const shippingEnabled = Boolean(input.shippingEnabled);
    const postage = shippingEnabled ? safeNumber(input.postage) : 0;
    const packaging = shippingEnabled ? safeNumber(input.packaging) : 0;
    const handling = shippingEnabled ? safeNumber(input.handling) : 0;
    const platformFeeRate = shippingEnabled ? safeNumber(input.platformFeeRate, 0.95) : 0;
    const wearRate = safeNumber(input.wearRate);
    const utilityRate = safeNumber(input.utilityRate);
    const maintenanceRate = safeNumber(input.maintenanceRate);
    const minimumProductionRate = safeNumber(input.minimumProductionRate);
    const failureRate = safeNumber(input.failureRate, 0.95);
    const marginRate = safeNumber(input.marginRate, 0.95);
    const marginFactor = Math.max(1 - marginRate, 0.05);

    const materialCost = materialRate * grams / 1000;
    const machineWear = printHours * wearRate;
    const utilities = printHours * utilityRate;
    const maintenance = printHours * maintenanceRate;
    const rawMachineCost = machineWear + utilities + maintenance;
    const failureReserve = (materialCost + rawMachineCost) * failureRate;
    const protectedProduction = Math.max(
      printHours * minimumProductionRate,
      rawMachineCost + failureReserve,
    );
    const designLabor = designHours * designRate;
    const handsLabor = handsMinutes / 60 * handsRate;

    const materialCharge = materialCost / marginFactor;
    const productionCharge = protectedProduction / marginFactor * complexity;
    const designCharge = designLabor / marginFactor * complexity;
    const handsCharge = handsLabor / marginFactor * complexity;
    const preShipping = materialCharge + productionCharge + designCharge + handsCharge;

    const directShipping = postage + packaging + handling;
    const shippingBase = directShipping / marginFactor;
    const preFeeTotal = preShipping + shippingBase;
    const total = shippingEnabled
      ? preFeeTotal / Math.max(1 - platformFeeRate, 0.05)
      : preShipping;
    const processorFee = shippingEnabled ? total * platformFeeRate : 0;
    const shippingCharge = shippingEnabled ? shippingBase + (total - preFeeTotal) : 0;

    const trueCost =
      materialCost +
      rawMachineCost +
      failureReserve +
      designLabor +
      handsLabor +
      directShipping +
      processorFee;
    const grossProfit = total - trueCost;
    const actualMargin = total > 0 ? grossProfit / total * 100 : 0;
    const perUnit = total / quantity;
    const targetMarginPercent = marginRate * 100;

    return {
      materialCost,
      machineWear,
      utilities,
      maintenance,
      rawMachineCost,
      failureReserve,
      protectedProduction,
      designLabor,
      handsLabor,
      postage,
      packaging,
      handling,
      processorFee,
      materialCharge,
      productionCharge,
      designCharge,
      handsCharge,
      shippingCharge,
      trueCost,
      total,
      quantity,
      perUnit,
      grossProfit,
      actualMargin,
      targetMarginPercent,
      belowTargetMargin: total > 0 && actualMargin + 0.01 < targetMarginPercent,
    };
  }

  return { calculateQuote };
});
